import {some} from 'lodash'
import {useCurrency} from 'providers/EnvironmentProvider'
import {useOptionalResult} from 'providers/ResultProvider'
import {useEffect, useMemo} from 'react'
import {Ventrata} from 'types'
import {ga4} from 'utils/ga4'
import {getDiscountDetails} from 'utils/getDiscountDetails'
import {getGa4OrderItems} from 'utils/getGa4OrderItems'
import {getHumanPrice} from 'utils/getHumanPrice'
import {getOrderUnitCounterMap} from 'utils/getOrderUnitCounterMap'
import {CartItems} from './Left/Left'
import {Right} from './Right/Right'
import {ConfirmationPageSubtitle} from './widgets/ConfirmationPageSubtitle'

type Props = {
  order: Ventrata.Order
}

export const ThankYou = ({order}: Props) => {
  const configCurrency = useCurrency()
  const currency = order.pricing?.currency ?? configCurrency
  const result = useOptionalResult()
  const paymentType = result?.paymentType ?? ''
  const crossSelling = useMemo(() => result?.crossSelling ?? {}, [result?.crossSelling])

  useEffect(() => {
    const firePurchaseEvent = () => {
      const purchaseKey = `ga4_purchase_${order.supplierReference}`

      if (sessionStorage.getItem(purchaseKey)) {
        return
      }

      const counterMap = getOrderUnitCounterMap(order)
      const discountDetails = getDiscountDetails(order)

      ga4({
        event: 'purchase',
        ecommerce: {
          currency,
          value: getHumanPrice(order.pricing?.retail),
          checkout_type: 'custom_checkout',

          tax: 0,
          shipping: 0,
          transaction_id: order.supplierReference,
          coupon: discountDetails ? discountDetails.name : '',
          payment_type: paymentType,
          total_tours: order.bookings.length,
          total_adult: counterMap.ADULT,
          total_child: counterMap.CHILD,
          total_infant: counterMap.INFANT,
          purchase_with_child: counterMap.CHILD > 0 ? 1 : 0,
          purchase_with_infant: counterMap.INFANT > 0 ? 1 : 0,
          purchase_with_upgrade: 0,
          purchase_with_cross: some(crossSelling) ? 1 : 0,
          items: getGa4OrderItems(order, currency, crossSelling),
          customer: [
            {
              first_name: order.contact.firstName,
              last_name: order.contact.lastName,
              email: order.contact.emailAddress,
              mobile: order.contact.phoneNumber,
              country: order.contact.country,
              postal_code: null,
              type: 'Order',
            },
          ],
        },
      })

      sessionStorage.setItem(purchaseKey, 'true')
    }

    if (document.readyState === 'complete') {
      firePurchaseEvent()
    } else {
      window.addEventListener('load', firePurchaseEvent)
      return () => window.removeEventListener('load', firePurchaseEvent)
    }
  }, [order, currency, paymentType, crossSelling])

  return (
    <div className="flex flex-col gap-8">
      <div className="font-bold text-[32px]">Thanks for booking with us!</div>
      <div>
        <ConfirmationPageSubtitle order={order} />
      </div>
      <div className="font-bold text-2xl">Order summary</div>

      <div className="flex flex-col d:flex-row justify-between">
        <CartItems order={order} />
        <Right order={order} />
      </div>
    </div>
  )
}
